import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/api";
import AdSlot from "@/components/AdSlot";
import { ArrowLeft, Globe, Mail, MapPin, Phone, ShieldCheck, Clock, Stethoscope } from "lucide-react";

// Fix default marker icons for Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const DENVER = [39.7392, -104.9903];

export default function ListingDetailPage() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get(`/listings/${id}`)
      .then((r) => active && setListing(r.data))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [id]);

  // Geocode (best effort) using OpenStreetMap Nominatim
  useEffect(() => {
    if (!listing) return;
    const parts = [listing.address, listing.city, listing.state, listing.zip, "USA"].filter(Boolean);
    if (parts.length < 2) {
      setCoords(DENVER);
      return;
    }
    const query = encodeURIComponent(parts.join(", "));
    fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${query}`, {
      headers: { Accept: "application/json" },
    })
      .then((r) => r.json())
      .then((arr) => {
        if (arr && arr.length > 0) {
          setCoords([parseFloat(arr[0].lat), parseFloat(arr[0].lon)]);
        } else {
          setCoords(DENVER);
        }
      })
      .catch(() => setCoords(DENVER));
  }, [listing]);

  if (loading) {
    return (
      <div className="container-page py-16" data-testid="listing-loading">
        <div className="h-72 w-full animate-pulse rounded-2xl bg-muted" />
        <div className="mt-8 h-10 w-2/3 animate-pulse rounded bg-muted" />
        <div className="mt-3 h-4 w-1/3 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="container-page py-16 text-center" data-testid="listing-not-found">
        <h1 className="font-display text-3xl font-bold text-primary">Listing not found</h1>
        <Link to="/directory" className="btn-secondary mt-6 inline-flex">Back to directory</Link>
      </div>
    );
  }

  const heroImg = listing.hero_url || null;
  const displayName = listing.practice_name ||
    [listing.first_name, listing.last_name].filter(Boolean).join(" ");
  const fullAddress = [listing.address, listing.city, listing.state, listing.zip].filter(Boolean).join(", ");
  const websiteUrl = listing.website
    ? listing.website.startsWith("http") ? listing.website : `https://${listing.website}`
    : null;
  const isVerified = listing.is_verified === true;

  return (
    <div data-testid="listing-detail-page">
      <div className="container-page pt-8">
        <Link to="/directory" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent" data-testid="back-link">
          <ArrowLeft className="h-4 w-4" /> Back to directory
        </Link>
      </div>

      {/* Hero — only when an actual hero image exists */}
      {heroImg ? (
        <section className="container-page mt-6">
          <div className="card-surface overflow-hidden">
            <div className="relative aspect-[21/9] min-h-[280px]">
              <img src={heroImg} alt={displayName} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/80 via-primary/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="chip bg-white/95">{listing.category}</span>
                  {isVerified && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-primary/95 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-foreground"
                      title="Verified by Black Health Resources"
                      data-testid="listing-verified-badge"
                    >
                      <ShieldCheck className="h-3 w-3" /> Verified by Black Health Resources
                    </span>
                  )}
                </div>
                <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-primary-foreground sm:text-5xl" data-testid="listing-title">
                  {displayName}
                </h1>
                {listing.specialty && (
                  <p className="mt-2 max-w-3xl text-base text-primary-foreground/85 sm:text-lg" data-testid="listing-specialty">
                    {listing.specialty}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="container-page mt-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="chip bg-primary/8 text-primary">{listing.category}</span>
            {isVerified && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-primary-foreground"
                title="Verified by Black Health Resources"
                data-testid="listing-verified-badge"
              >
                <ShieldCheck className="h-3 w-3" /> Verified by Black Health Resources
              </span>
            )}
          </div>
          <h1 className="mt-3 font-display text-4xl font-bold leading-tight text-primary sm:text-5xl" data-testid="listing-title">
            {displayName}
          </h1>
          {listing.specialty && (
            <p className="mt-2 max-w-3xl text-base text-foreground/75 sm:text-lg" data-testid="listing-specialty">
              {listing.specialty}
            </p>
          )}
        </section>
      )}

      {/* Body */}
      <section className="container-page mt-10 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-8">
          {listing.description && (
            <div>
              <h2 className="font-display text-2xl font-bold text-primary">About</h2>
              <p className="mt-3 whitespace-pre-line text-foreground/80" data-testid="listing-description">
                {listing.description}
              </p>
            </div>
          )}
          {listing.notes && listing.notes !== listing.description && (
            <div>
              <h2 className="font-display text-2xl font-bold text-primary">Notes</h2>
              <p className="mt-3 whitespace-pre-line text-foreground/80" data-testid="listing-notes">
                {listing.notes}
              </p>
            </div>
          )}
          {(listing.insurance_accepted || listing.accepts_medicaid || listing.accepts_medicare) && (
            <div>
              <h2 className="font-display text-2xl font-bold text-primary">Insurance & coverage</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {listing.accepts_medicaid && <span className="rounded-md bg-primary/8 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary">Medicaid</span>}
                {listing.accepts_medicare && <span className="rounded-md bg-primary/8 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary">Medicare</span>}
              </div>
              {listing.insurance_accepted && (
                <p className="mt-3 text-foreground/80" data-testid="listing-insurance">{listing.insurance_accepted}</p>
              )}
            </div>
          )}
          {/* Map */}
          {fullAddress && (
            <div>
              <h2 className="font-display text-2xl font-bold text-primary">Location</h2>
              <p className="mt-2 inline-flex items-center gap-1.5 text-foreground/80" data-testid="listing-address">
                <MapPin className="h-4 w-4 text-accent-dark" /> {fullAddress}
              </p>
              <div className="mt-4 overflow-hidden rounded-xl border border-border" data-testid="listing-map">
                {coords && (
                  <MapContainer
                    center={coords}
                    zoom={13}
                    scrollWheelZoom={false}
                    style={{ height: 320, width: "100%" }}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    <Marker position={coords}>
                      <Popup>
                        <strong>{displayName}</strong>
                        <br />
                        {fullAddress}
                      </Popup>
                    </Marker>
                  </MapContainer>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="card-surface p-6" data-testid="listing-contact">
            <h3 className="font-display text-xl font-bold text-primary">Contact</h3>
            <ul className="mt-4 space-y-3 text-sm">
              {listing.phone && (
                <li className="flex items-start gap-3">
                  <Phone className="mt-0.5 h-4 w-4 text-accent-dark" />
                  <a href={`tel:${listing.phone.replace(/[^0-9+]/g, "")}`} className="font-semibold text-primary hover:text-accent" data-testid="listing-phone">
                    {listing.phone}
                  </a>
                </li>
              )}
              {listing.email && (
                <li className="flex items-start gap-3">
                  <Mail className="mt-0.5 h-4 w-4 text-accent-dark" />
                  <a href={`mailto:${listing.email}`} className="break-all text-primary hover:text-accent" data-testid="listing-email">
                    {listing.email}
                  </a>
                </li>
              )}
              {websiteUrl && (
                <li className="flex items-start gap-3">
                  <Globe className="mt-0.5 h-4 w-4 text-accent-dark" />
                  <a href={websiteUrl} target="_blank" rel="noreferrer" className="break-all text-primary hover:text-accent" data-testid="listing-website">
                    {listing.website}
                  </a>
                </li>
              )}
              {listing.hours && (
                <li className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-4 w-4 text-accent-dark" />
                  <span className="text-foreground/80" data-testid="listing-hours">{listing.hours}</span>
                </li>
              )}
              {listing.telehealth && (
                <li className="flex items-start gap-3">
                  <Stethoscope className="mt-0.5 h-4 w-4 text-accent-dark" />
                  <span className="text-foreground/80">Telehealth: {listing.telehealth}</span>
                </li>
              )}
            </ul>
          </div>

          {listing.status === "unclaimed" && (
            <div className="card-surface p-6" data-testid="claim-cta">
              <h3 className="font-display text-xl font-bold text-primary">Are you the owner?</h3>
              <p className="mt-2 text-sm text-foreground/70">
                Claim this listing to add photos, services, hours, blog posts and respond to inquiries.
              </p>
              <Link to={`/listing/${listing.id}/claim`} className="btn-accent mt-4 w-full" data-testid="claim-button">
                Claim this listing
              </Link>
            </div>
          )}
          <div className="mt-4"><AdSlot placement="sidebar" /></div>
        </aside>
      </section>
      <LatestPosts listingId={listing.id} />
    </div>
  );
}

function LatestPosts({ listingId }) {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    import("@/lib/api").then(({ api }) => {
      api.get("/blog", { params: { listing_id: listingId, page_size: 3 } })
        .then((r) => setPosts(r.data.results || []));
    });
  }, [listingId]);
  if (!posts.length) return null;
  return (
    <section className="container-page mt-16" data-testid="latest-posts">
      <h2 className="font-display text-2xl font-bold text-primary">Latest from this listing</h2>
      <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {posts.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} className="card-surface card-hover overflow-hidden">
            {p.hero_url && (
              <div className="aspect-[16/10] overflow-hidden bg-muted">
                <img src={p.hero_url.startsWith("http") ? p.hero_url : `${process.env.REACT_APP_BACKEND_URL}${p.hero_url}`} alt={p.title} className="h-full w-full object-cover" />
              </div>
            )}
            <div className="p-5">
              <h3 className="font-display text-lg font-bold text-primary line-clamp-2">{p.title}</h3>
              <p className="mt-2 text-xs text-muted-foreground">{new Date(p.published_at).toLocaleDateString()}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
